import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail, MapPin } from 'lucide-react';
import { LanguageSwitcher } from '../shared/LanguageSwitcher';
import { BrandMark } from '../shared/BrandMark';
import { useAuth } from '../../contexts/AuthContext';

interface FooterLink {
  label: string;
  href: string;
  isAnchor?: boolean;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

export const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  const footerLinks: Record<string, FooterSection> = {
    product: {
      title: t('footer.product'),
      links: [
        { label: t('header.sections.reading'), href: '#lezen', isAnchor: true },
        { label: t('header.sections.listening'), href: '#luisteren', isAnchor: true },
        { label: t('header.sections.writing'), href: '#schrijven', isAnchor: true },
        { label: t('header.sections.speaking'), href: '#spreken', isAnchor: true },
        { label: t('header.pricing'), href: '#pricing', isAnchor: true },
      ]
    },
    account: {
      title: t('footer.account'),
      links: isAuthenticated
        ? [
            { label: t('header.dashboard'), href: '/dashboard' },
            { label: t('auth.resetPassword.request.cardTitle'), href: '/reset-password' },
          ]
        : [
            { label: t('header.login'), href: '/login' },
            { label: t('header.register'), href: '/register' },
            { label: t('header.tryFree'), href: '/try-free' },
            { label: t('auth.resetPassword.request.cardTitle'), href: '/reset-password' },
          ]
    },
    company: {
      title: t('footer.company'),
      links: [
        { label: t('footer.about'), href: '/about' },
        { label: t('footer.contact'), href: '/contact' },
        { label: t('header.faq'), href: '#faq', isAnchor: true },
        { label: t('footer.helpCenter'), href: '/help' },
      ]
    },
    legal: {
      title: t('footer.legal'),
      links: [
        { label: t('footer.terms'), href: '/terms' },
        { label: t('footer.privacy'), href: '/privacy' },
        { label: t('footer.cookies'), href: '/cookies' },
        { label: t('footer.accessibility'), href: '/accessibility' },
      ]
    }
  };

  const handleLinkClick = (href: string, isAnchor?: boolean) => {
    if (isAnchor) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="border-t border-primary/10 bg-[#121b19] text-white">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-14 md:grid-cols-2 lg:grid-cols-6">
        <div className="lg:col-span-2">
          <BrandMark theme="dark" size="lg" className="mb-6" />
          <p className="max-w-sm text-sm leading-relaxed text-white/70">
            {t('footer.tagline')}
          </p>
          <div className="mt-6 space-y-3 text-sm text-white/70">
            <a href="mailto:support@inburgertest.nl" className="flex items-center gap-2 transition-colors hover:text-white">
              <Mail className="h-4 w-4" />
              support@inburgertest.nl
            </a>
            <p className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {t('footer.location')}
            </p>
          </div>
        </div>

        {Object.values(footerLinks).map((section) => (
          <div key={section.title}>
            <h4 className="mb-4 text-sm font-semibold uppercase tracking-[0.16em] text-white/90">{section.title}</h4>
            <ul className="space-y-3">
              {section.links.map((link) => (
                <li key={link.label}>
                  {link.isAnchor ? (
                    <button
                      onClick={() => handleLinkClick(link.href, link.isAnchor)}
                      className="text-sm text-white/70 transition-colors hover:text-white"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-sm text-white/70 transition-colors hover:text-white"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-6 md:flex-row">
          <p className="text-xs text-white/60">
            © {new Date().getFullYear()} {t('common.appName')}. {t('footer.allRightsReserved')}.
          </p>

          <LanguageSwitcher variant="compact" className="text-white/70" />
        </div>
      </div>

      <div className="border-t border-white/10 bg-[#0d1413]">
        <div className="mx-auto max-w-7xl px-6 py-4">
          <div className="flex flex-wrap justify-center gap-6 text-xs text-white/55">
            <span>{t('footer.securityBadges.ssl')}</span>
            <span>{t('footer.securityBadges.payment')}</span>
            <span>{t('footer.securityBadges.gdpr')}</span>
          </div>
          <p className="mt-3 text-center text-xs text-white/45">
            {t('common.independenceDisclaimer')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
